import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AIJobStatus, PlainText, Source, SourceMaterialScraping, SourceMaterialsMediaObject, Website } from 'types'
import { useCreateSource } from './useCreateSource'

interface Data {
  source: Source
}

type Variables =
  | Pick<PlainText, 'text' | 'type'>
  | (Pick<Website, 'url' | 'type'> & { scrape_images?: boolean })
  | (Pick<SourceMaterialsMediaObject, 'type'> & { media_object_id: number })

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

export class SourceMaterialScrapingJobFailureError extends Error {
  constructor(failureReason?: string) {
    super(failureReason ?? 'The source material scraping job failed.')
    this.name = 'SourceMaterialScrapingJobFailureError'
  }
}

export const useCreateSourceSync = () => {
  const workspaceId = useCurrentWorkspaceId()
  const { mutateAsync: createSource } = useCreateSource()
  return useMutation<Data, Error, Variables>(async (vars) => {
    const request = await createSource(vars)

    if (!request.source.pending_or_started_source_material_scraping) {
      return request
    }

    let scraping = request.source.pending_or_started_source_material_scraping
    let interval = 500

    do {
      const res = await sendToApi<{ source_material_scraping: SourceMaterialScraping }>(
        `/w/${workspaceId}/source_material_scrapings/${scraping.id}`,
      )

      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error()
      }

      scraping = res.data.source_material_scraping

      if (scraping?.status === AIJobStatus.Failed) {
        const error = scraping.failure_reason
          ? new SourceMaterialScrapingJobFailureError(scraping.failure_reason)
          : new Error('Failed to scrape source materials')
        throw error
      }

      await new Promise((resolve) => {
        setTimeout(resolve, interval)
      })
    } while (![AIJobStatus.Successful, AIJobStatus.Failed].includes(scraping?.status))

    const sourceResponse = await sendToApi<Data>(
      `/w/${workspaceId}/sources/${request.source.id}?type=${request.source.type}`,
    )

    if (sourceResponse.type !== SUCCESS_RESPONSE_TYPE) {
      throw new Error(sourceResponse.data)
    }

    return sourceResponse.data
  })
}
